"use client"

/**
 * Cloudflare Turnstile widget — client-side rendering + token capture.
 *
 * - Loads the Turnstile script (api.js) once globally via next/script.
 * - Polls for `window.turnstile` (the script's global) directly. This is more
 *   reliable than relying on `<Script onReady>`, which only fires ONCE per
 *   script tag lifetime — and React 19 Strict Mode's double-mount renders the
 *   `<Script>` element twice in dev. The second instance's onReady never fires
 *   because the script + callback already fired during the first mount.
 *   Polling the global works regardless of which mount is live.
 * - Each <TurnstileWidget/> calls window.turnstile.render() on its container.
 * - Calls onToken(token) whenever a fresh token is issued; calls onExpire()
 *   when the token ages out; onError() on hard failures.
 * - Sets `response-field: true` so Cloudflare writes the token into its own
 *   internal input + propagates to any same-name input on the page (including
 *   the one TurnstileGate renders), so FormData always sees the token without
 *   depending on React state being flushed.
 */

import Script from "next/script"
import { useEffect, useRef } from "react"

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          callback?: (token: string) => void
          "expired-callback"?: () => void
          "error-callback"?: () => void
          theme?: "light" | "dark" | "auto"
          size?: "normal" | "compact" | "flexible"
          "response-field"?: boolean
        }
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ""

type Props = {
  /** Called with the token whenever a fresh token is issued. */
  onToken: (token: string) => void
  /** Called when the token expires (visitor must re-verify). */
  onExpire?: () => void
  /** Called on hard failure (script load error, widget render error). */
  onError?: () => void
  /** Optional className for the widget container. */
  className?: string
}

export default function TurnstileWidget({
  onToken,
  onExpire,
  onError,
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onTokenRef = useRef(onToken)
  const onExpireRef = useRef(onExpire)
  const onErrorRef = useRef(onError)
  const mountedRef = useRef(true)

  // Keep the latest callbacks without re-rendering the widget on every render.
  useEffect(() => {
    onTokenRef.current = onToken
    onExpireRef.current = onExpire
    onErrorRef.current = onError
  }, [onToken, onExpire, onError])

  // Track whether this effect instance is still live (handles StrictMode's
  // double-mount: when the first instance unmounts, it sets its ref to false
  // so it doesn't try to render the widget from a dead effect).
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current) return
    if (!SITE_KEY) {
      console.warn("[Turnstile] NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set")
      onErrorRef.current?.()
      return
    }

    // Re-attempt if the script hasn't loaded yet on first mount. We keep
    // trying on a short interval until either the API is available or the
    // component unmounts. 4s hard timeout emits a single onError to avoid
    // spinning forever on a failed script load.
    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const startedAt = Date.now()

    const tryRender = () => {
      if (cancelled || !mountedRef.current) return
      const t = window.turnstile
      const el = containerRef.current
      if (!t || !el || widgetIdRef.current) return

      try {
        widgetIdRef.current = t.render(el, {
          sitekey: SITE_KEY,
          callback: (token: string) => onTokenRef.current(token),
          "expired-callback": () => onExpireRef.current?.(),
          "error-callback": () => onErrorRef.current?.(),
          theme: "light",
          size: "normal",
          "response-field": true,
        })
      } catch (err) {
        console.error("[Turnstile] render() threw", err)
        onErrorRef.current?.()
      }
    }

    if (window.turnstile) {
      tryRender()
    } else {
      const interval = setInterval(() => {
        if (cancelled || !mountedRef.current) return clearInterval(interval)
        if (window.turnstile) {
          clearInterval(interval)
          tryRender()
        } else if (Date.now() - startedAt > 4000) {
          clearInterval(interval)
          onErrorRef.current?.()
        }
      }, 80)
      return () => clearInterval(interval)
    }

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          /* ignore — widget may already be torn down */
        }
        widgetIdRef.current = null
      }
    }
  }, [])

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        async
        defer
        strategy="afterInteractive"
        onError={() => onErrorRef.current?.()}
      />
      <div ref={containerRef} className={className} />
    </>
  )
}

/**
 * Hidden input Cloudflare populates with the token. We also include the field
 * even before the widget mounts so the form's serialized payload always has a
 * stable name. The value is replaced by Turnstile on success.
 */
export function TurnstileTokenField() {
  return (
    <input
      type="hidden"
      name="cf-turnstile-response"
      defaultValue=""
      data-turnstile-token
    />
  )
}